import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import api from '../lib/api'

export type PlanType = 'free' | 'pro' | 'business'
export type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'cancelled' | 'expired'

export interface PlanLimits {
  max_proposals: number
  ai_enabled: boolean
  pdf_enabled: boolean
  templates_enabled: boolean
  email_notifications: boolean
}

export interface SubscriptionInfo {
  plan: PlanType
  status: SubscriptionStatus
  paypal_subscription_id?: string
  current_period_start?: string
  current_period_end?: string
  price_amount: number
  price_currency: string
  cancelled_at?: string
}

export interface UsageInfo {
  proposals_count: number
  proposals_limit: number
  proposals_remaining: number
}

interface SubscriptionContextValue {
  subscription: SubscriptionInfo | null
  limits: PlanLimits
  usage: UsageInfo
  loading: boolean
  isPro: boolean
  isBusiness: boolean
  canCreateProposal: boolean
  canUseAI: boolean
  canExportPDF: boolean
  canUseTemplates: boolean
  refresh: () => Promise<void>
}

const FREE_LIMITS: PlanLimits = {
  max_proposals: 3,
  ai_enabled: false,
  pdf_enabled: false,
  templates_enabled: false,
  email_notifications: false,
}

const DEFAULT_USAGE: UsageInfo = {
  proposals_count: 0,
  proposals_limit: 3,
  proposals_remaining: 3,
}

const SubscriptionContext = createContext<SubscriptionContextValue>({
  subscription: null,
  limits: FREE_LIMITS,
  usage: DEFAULT_USAGE,
  loading: true,
  isPro: false,
  isBusiness: false,
  canCreateProposal: true,
  canUseAI: false,
  canExportPDF: false,
  canUseTemplates: false,
  refresh: async () => {},
})

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null)
  const [limits, setLimits] = useState<PlanLimits>(FREE_LIMITS)
  const [usage, setUsage] = useState<UsageInfo>(DEFAULT_USAGE)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    if (!user) {
      setSubscription(null)
      setLimits(FREE_LIMITS)
      setUsage(DEFAULT_USAGE)
      setLoading(false)
      return
    }

    try {
      const data = await api.getSubscription()
      if (data.subscription) {
        setSubscription(data.subscription as SubscriptionInfo)
      }
      if (data.limits) {
        setLimits(data.limits as PlanLimits)
      }
      if (data.usage) {
        setUsage(data.usage as UsageInfo)
      }
    } catch (err) {
      console.error('Failed to load subscription:', err)
      setSubscription({ plan: 'free', status: 'active', price_amount: 0, price_currency: 'USD' })
      setLimits(FREE_LIMITS)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    refresh()
  }, [refresh])

  const isActiveOrCancelled = subscription?.status === 'active' || subscription?.status === 'cancelled'
  const isPro = (subscription?.plan === 'pro' || subscription?.plan === 'business') && isActiveOrCancelled
  const isBusiness = subscription?.plan === 'business' && isActiveOrCancelled
  const canCreateProposal = isPro || usage.proposals_remaining > 0 || usage.proposals_remaining === -1
  const canUseAI = limits.ai_enabled
  const canExportPDF = limits.pdf_enabled
  const canUseTemplates = limits.templates_enabled

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        limits,
        usage,
        loading,
        isPro,
        isBusiness,
        canCreateProposal,
        canUseAI,
        canExportPDF,
        canUseTemplates,
        refresh,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  )
}

export function useSubscription() {
  return useContext(SubscriptionContext)
}
